import ClearIcon from "@mui/icons-material/Clear";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Box, Button, useTheme } from "@mui/material";
import useAddressForm from "components/shared/address/controller";
import { useLoadingToast } from "hooks/toast/toasts";
import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import CustomLoadingButton from "../../shared/button/CustomLoadingButton";
import { FormatCNPJ, FormatCPF } from "../../shared/format/Formats";
import AddressForm from "../../shared/address/AddressForm";
import CustomTextField from "../../shared/textFields/CustomTextField";
import FormatedCustomTextField from "../../shared/textFields/FormatedCustomTextField";
import CustomRadioGroup from "../CustomRadioGroup";
import useDedicatedAttorneyForm from "./controller";
import { downloadFileFromApi } from "services/pdf/download-file-from-api";
import PowerDistributionCompaniesAutoComplete from "components/shared/autoCompletes/PowerDistributionCompaniesAutoComplete";
import CustomAutoComplete from "components/shared/autoCompletes/CustomAutoComplete";
import { createAttorneyPdfWithoutProject } from "services/api/api";

export default function AttorneyDedicatedForm() {
  const theme = useTheme();

  const controller = useDedicatedAttorneyForm();
  const addressController = useAddressForm();
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();
  const [isAttorneySending, setIsAttorneySending] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [requestLoading, setRequestLoading] = useState(false);

  const handleSendAttorney = async (event) => {
    // Validar campos antes de enviar
    const fieldsValid = controller.validateFields();
    const addressValid = addressController.validatePartial(['zipCode', 'city', 'state']);
    
    if (!fieldsValid || !addressValid) {
      return;
    }

    let sendAttorneyToastId;
    setIsAttorneySending(true);
    setIsDisabled(true);
    try {
      sendAttorneyToastId = initializeLoadingToast({
        text: `Gerando Procuração...`,
      });

      const project = controller.values();
      const address = addressController.values();

      const fileName = `Procuração - ${project.projectName} - ${project.client.name}.pdf`;

      const body = controller.returnBody(address);

      const { status, data } = await createAttorneyPdfWithoutProject(body);

      if (status === 200) {
        updateLoadingToast({
          id: sendAttorneyToastId,
          text: `Procuração gerada com sucesso`,
          type: "success",
        });

        downloadFileFromApi(data, fileName);
      }
    } catch (error) {
      updateLoadingToast({
        id: sendAttorneyToastId,
        text: error.message ?? `Erro ao gerar procuração, tente novamente`,
        type: "error",
      });
    } finally {
      setIsAttorneySending(false);
      setIsDisabled(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 800,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <ToastContainer />

      <CustomAutoComplete
        params={controller.client}
        options={controller.apiClients}
        disabled={false}
        label="Selecione o Integrador"
        width="40%"
      />

      {controller.clientValue && (
        <React.Fragment>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mt: 2 }}>
            <CustomRadioGroup
              radioGroupParams={controller.clientType}
              label={"Tipo de cliente:"}
              radioValues={["PERSON", "ENTITY"]}
              radioLabels={["Pessoa Física", "Pessoa Jurídica"]}
            />

            <Button
              variant="outlined"
              color="error"
              sx={{
                height: 30,
                alignSelf: "flex-end",
              }}
              disabled={isDisabled}
              onClick={() => {
                controller.clearAllFields();
                addressController.clearAllFields();
              }}
              endIcon={<ClearIcon />}
            >
              Limpar
            </Button>
          </Box>

          <Box
            sx={{
              minHeight: 400,
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Box
              sx={{
                width: "48%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CustomTextField
                params={controller.projectName}
                disabled={isDisabled}
                label="Nome do Cliente"
                maxLength={50}
              />

              <PowerDistributionCompaniesAutoComplete
                params={{
                  ...controller.powerDistributionCompany,
                  error: controller.powerDistributionCompany.error,
                  errorLabel: controller.powerDistributionCompany.errorLabel
                }}
                options={controller.clientsPDCs}
                disabled={isDisabled}
                label="Concessionária"
                width="100%"
              />

              <CustomAutoComplete
                params={controller.engineer}
                options={controller.apiEngineers}
                disabled={isDisabled}
                label="Engenheiro"
                width="100%"
              />

              {controller?.clientType?.value === "ENTITY" && (
                <React.Fragment>
                  <FormatedCustomTextField
                    params={controller.cnpj}
                    disabled={isDisabled}
                    label="CNPJ"
                    inputcomponent={FormatCNPJ}
                  />
                  <CustomTextField
                    params={controller.repName}
                    disabled={isDisabled}
                    label="Nome - Representante Legal"
                  />
                  <CustomTextField
                    params={controller.repNationality}
                    disabled={isDisabled}
                    label="Nacionalidade - Representante Legal"
                  />
                </React.Fragment>
              )}
              <FormatedCustomTextField
                params={controller.cpf}
                disabled={isDisabled}
                label={
                  controller?.clientType?.value === "ENTITY" ? "CPF - Representante Legal" : "CPF"
                }
                inputcomponent={FormatCPF}
              />
              <CustomTextField
                params={controller.rg}
                disabled={isDisabled}
                label={
                  controller?.clientType?.value === "ENTITY" ? "RG - Representante Legal" : "RG"
                }
              />
              <CustomTextField
                params={controller.issuingBody}
                disabled={isDisabled}
                label={
                  controller?.clientType?.value === "ENTITY"
                    ? "Órgão Expedidor - Representante Legal"
                    : "Órgão Expedidor"
                }
              />
            </Box>
            <Box
              sx={{
                width: "48%",
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <AddressForm controller={addressController} required={false} />

              <CustomLoadingButton
                startIcon={<PictureAsPdfIcon />}
                buttonText="Gerar Procuração"
                buttonLoadingText="Gerando..."
                disabled={isDisabled}
                handleButtonClick={handleSendAttorney}
                requestLoading={isAttorneySending}
              />
            </Box>
          </Box>
        </React.Fragment>
      )}
    </Box>
  );
}
